import React, { useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { ShieldCheck, RefreshCw, KeyRound, Eye, Copy, Trash2, Plus, Radar, AlertTriangle, Users } from 'lucide-react'
import AgentMesh from '../components/AgentMesh'

// Platform operator console — every workspace and who it's allocated to.
// Access is gated server-side by ADMIN_EMAILS; non-admins get a 403 here.
export default function Admin() {
  const [tenants, setTenants] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true); setErr(null)
    axios.get('/api/v1/admin/tenants')
      .then(r => setTenants(r.data?.tenants ?? []))
      .catch(e => setErr(e?.response?.status === 403 ? 'You are not a platform admin.' : 'Could not load workspaces.'))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  // ── Platform secrets/config (admin-managed, encrypted in the DB) ──
  const [settings, setSettings] = useState(null)
  const [draft, setDraft] = useState({})
  const [savingKey, setSavingKey] = useState(null)
  const loadSettings = () => {
    axios.get('/api/v1/admin/settings').then(r => setSettings(r.data?.settings || [])).catch(() => setSettings([]))
  }
  useEffect(loadSettings, [])
  const saveSetting = async (key) => {
    setSavingKey(key)
    try {
      const { data } = await axios.put('/api/v1/admin/settings', { key, value: draft[key] ?? '' })
      setSettings(data.settings || [])
      setDraft(d => ({ ...d, [key]: '' }))
      toast.success('Saved')
    } catch (e) { toast.error(e?.response?.data?.error || 'Save failed') }
    finally { setSavingKey(null) }
  }
  const sourceBadge = (s) => s === 'db' ? 'bg-emerald-500/10 text-emerald-600'
    : s === 'env' ? 'bg-blue-500/10 text-blue-600' : 'bg-gray-100 text-gray-500'

  // Full CRUD on env vars / secrets.
  const [revealed, setRevealed] = useState({})
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const reveal = async (key) => {
    try { const { data } = await axios.get(`/api/v1/admin/settings/${key}/reveal`); setRevealed(r => ({ ...r, [key]: data.value ?? '' })); setDraft(d => ({ ...d, [key]: data.value ?? '' })) }
    catch { toast.error('Could not reveal') }
  }
  const copyVal = async (key, fallback) => {
    let v = revealed[key] ?? fallback
    if (v == null || v === '') { try { const { data } = await axios.get(`/api/v1/admin/settings/${key}/reveal`); v = data.value } catch {} }
    navigator.clipboard.writeText(v || ''); toast.success('Copied')
  }
  const removeSetting = async (key) => {
    if (!window.confirm(`Delete ${key}? Anything using it stops working.`)) return
    try { const { data } = await axios.delete(`/api/v1/admin/settings/${key}`); setSettings(data.settings || []); toast.success('Deleted') }
    catch (e) { toast.error(e?.response?.data?.error || 'Delete failed') }
  }
  const addSetting = async () => {
    if (!newKey.trim()) return
    try {
      const { data } = await axios.put('/api/v1/admin/settings', { key: newKey.trim().toUpperCase(), value: newValue })
      setSettings(data.settings || []); setNewKey(''); setNewValue(''); toast.success('Added')
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed') }
  }
  const groupsOf = (list) => list.reduce((acc, s) => { (acc[s.group] = acc[s.group] || []).push(s); return acc }, {})

  // ── Platform oversight (agents/oversightAgent.js) — watchtower sweep ──
  const [oversight, setOversight] = useState(null)
  useEffect(() => {
    axios.get('/api/v1/admin/oversight')
      .then(r => setOversight(r.data || { tenants: 0, flags: [] }))
      .catch(() => setOversight(null))
  }, [])
  const sevBadge = (s) => s === 'high' ? 'bg-red-500/10 text-red-600'
    : s === 'medium' ? 'bg-amber-500/10 text-amber-600' : 'bg-gray-100 text-gray-500'

  // ── AI Models (live tier→model catalog — flip free↔paid, no redeploy) ──
  const [models, setModels] = useState(null)
  const [mDraft, setMDraft] = useState({})
  const [mBusy, setMBusy] = useState(null)
  const [mTest, setMTest] = useState({})
  const loadModels = () => axios.get('/api/v1/admin/models').then(r => { setModels(r.data); setMDraft({}) }).catch(() => setModels(null))
  useEffect(loadModels, [])
  const saveModel = async (key, id) => {
    if (!id || !id.includes('/')) { toast.error('Model id must look like "vendor/model"'); return }
    setMBusy(key)
    try { await axios.put('/api/v1/admin/models', { key, id }); toast.success(`${key} → ${id}`); loadModels() }
    catch (e) { toast.error(e?.response?.data?.error || 'Save failed') } finally { setMBusy(null) }
  }
  const applyPreset = async (name) => {
    if (!window.confirm(`Switch ALL agents to the ${name.toUpperCase()} models? Takes effect live, no redeploy.`)) return
    setMBusy(`preset:${name}`)
    try { await axios.post('/api/v1/admin/models/preset', { name }); toast.success(`Switched to ${name} models`); loadModels() }
    catch (e) { toast.error(e?.response?.data?.error || 'Preset failed') } finally { setMBusy(null) }
  }
  const testModel = async (id) => {
    setMTest(t => ({ ...t, [id]: { loading: true } }))
    try { const { data } = await axios.post('/api/v1/admin/models/test', { id }); setMTest(t => ({ ...t, [id]: data })) }
    catch (e) { setMTest(t => ({ ...t, [id]: { ok: false, error: e?.response?.data?.error || 'failed' } })) }
  }

  // ── Platform agent mesh (the full agent catalog) for the AgentMesh diagram ──
  const [agentCatalog, setAgentCatalog] = useState([])
  useEffect(() => {
    axios.get('/api/v1/admin/agent-catalog')
      .then(r => setAgentCatalog(r.data?.agents || []))
      .catch(() => setAgentCatalog([]))
  }, [])

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—')
  const statusColor = (t) =>
    t.isolation_tier === 'provisioning' ? 'text-amber-600'
    : t.status === 'active' ? 'text-emerald-600'
    : t.status === 'past_due' ? 'text-red-500' : 'text-gray-500'

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-2">
        <ShieldCheck size={20} className="text-primary-600" />
        <h2 className="text-2xl font-bold text-gray-900">Admin</h2>
        <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      <p className="text-gray-500 mt-1">Every workspace on the platform and who it's allocated to.</p>

      {agentCatalog.length > 0 && (
        <div className="mt-6">
          <AgentMesh
            agents={agentCatalog}
            runsUrl={null}
            title="Platform agent mesh"
            subtitle="Every agent the platform runs — the Chief and the specialist agents it dispatches, and the sub-agents each one commands. Click any node to inspect it."
          />
        </div>
      )}

      {err && <div className="card mt-6 text-sm text-red-500">{err}</div>}

      {!err && (
        <div className="card mt-6 p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="px-4 py-3 font-semibold">Workspace</th>
                <th className="px-4 py-3 font-semibold">Owner</th>
                <th className="px-4 py-3 font-semibold">Plan</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Users</th>
                <th className="px-4 py-3 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {tenants === null ? (
                <tr><td colSpan={6} className="px-4 py-6 text-gray-500">Loading…</td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-gray-500">No workspaces yet.</td></tr>
              ) : tenants.map((t) => (
                <tr key={t.namespace_id} className="border-b border-gray-200/60 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{t.name || t.slug}</div>
                    <div className="text-[11px] text-gray-400 font-mono">{t.namespace_id}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{t.owner_email || t.billing_email || '—'}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">{t.plan}</span></td>
                  <td className={`px-4 py-3 font-medium ${statusColor(t)}`}>{t.isolation_tier === 'provisioning' ? 'provisioning' : t.status}</td>
                  <td className="px-4 py-3 text-gray-700">{t.user_count}</td>
                  <td className="px-4 py-3 text-gray-500">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {Array.isArray(tenants) && <p className="text-xs text-gray-400 mt-3">{tenants.length} workspace(s).</p>}

      {/* Platform oversight — the Oversight Agent's watchtower sweep across every workspace */}
      {oversight && (
        <>
          <div className="flex items-center gap-2 mt-12">
            <Radar size={20} className="text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">Platform oversight</h2>
            <span className="ml-auto text-sm text-gray-500">{oversight.tenants ?? 0} workspace(s) swept · {(oversight.flags?.length ?? 0)} flag(s)</span>
          </div>
          <p className="text-gray-500 mt-1">Automated due diligence — stalled provisioning, dormant workspaces, and degraded connectors, flagged across the whole platform.</p>
          {(oversight.flags?.length ?? 0) === 0 ? (
            <div className="card mt-6 text-sm text-emerald-600 flex items-center gap-2">
              <ShieldCheck size={16} /> All clear — no issues flagged across {oversight.tenants ?? 0} workspace(s).
            </div>
          ) : (
            <div className="card mt-6 divide-y divide-gray-200 p-0">
              {oversight.flags.map((f, i) => (
                <div key={i} className="px-5 py-4 flex flex-wrap items-center gap-3">
                  <AlertTriangle size={16} className={f.severity === 'high' ? 'text-red-500' : 'text-amber-500'} />
                  <div className="min-w-[180px]">
                    <div className="font-medium text-gray-900">{f.tenant}</div>
                    <div className="text-[11px] text-gray-400 font-mono">{f.kind}</div>
                  </div>
                  <div className="text-sm text-gray-600 flex-1 min-w-[200px]">{f.detail}</div>
                  <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${sevBadge(f.severity)}`}>{f.severity}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Customers needing attention — the Oversight Agent's cross-tenant lifecycle view */}
      {oversight && Array.isArray(oversight.customers) && (
        <>
          <div className="flex items-center gap-2 mt-12">
            <Users size={20} className="text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">Customers needing attention</h2>
            <span className="ml-auto text-sm text-gray-500">{oversight.customersScanned ?? 0} customer(s) checked · {oversight.customers.length} flagged</span>
          </div>
          <p className="text-gray-500 mt-1">Per-customer lifecycle across every workspace — failed provisions, empty knowledge bases, unpaid or awaiting-payment services, uncontacted leads, and billing status.</p>
          {oversight.customers.length === 0 ? (
            <div className="card mt-6 text-sm text-emerald-600 flex items-center gap-2">
              <ShieldCheck size={16} /> Every customer's lifecycle looks healthy.
            </div>
          ) : (
            <div className="card mt-6 divide-y divide-gray-200 p-0">
              {oversight.customers.map((c) => (
                <div key={c.namespaceId} className="px-5 py-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-[200px]">
                      <div className="font-medium text-gray-900">{c.name}</div>
                      <div className="text-[11px] text-gray-400 font-mono">{c.namespaceId}</div>
                    </div>
                    <div className="text-sm text-gray-600 flex-1 min-w-[160px]">{c.email || '—'}</div>
                    {c.plan && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">{c.plan}</span>}
                    {c.status && (
                      <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${c.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'}`}>{c.status}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2.5">
                    {(c.flags || []).map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-700 border border-amber-200/60">
                        <AlertTriangle size={12} className="shrink-0" /> {f}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* AI Models — live tier→model control (flip free↔paid, no redeploy) */}
      <div className="flex items-center gap-2 mt-12">
        <Radar size={20} className="text-primary-600" />
        <h2 className="text-2xl font-bold text-gray-900">AI Models</h2>
      </div>
      <p className="text-gray-500 mt-1">Which model each agent tier runs on — <b>editable live, no redeploy</b>. Flip the whole platform between free and paid in one click, or set any tier to a specific OpenRouter model.</p>
      {models === null ? (
        <div className="card mt-6 text-sm text-gray-500">Loading models…</div>
      ) : (
        <>
          <div className="card mt-6 flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-600 font-medium">Preset:</span>
            {['free', 'paid'].map((name) => (
              <button key={name} onClick={() => applyPreset(name)} disabled={mBusy === `preset:${name}`}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors disabled:opacity-50 ${models.activePreset === name ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 text-gray-700 hover:border-primary-400'}`}>
                {name === 'free' ? 'Free ($0)' : 'Paid (Claude / DeepSeek / Gemini)'}{models.activePreset === name ? ' · active' : ''}
              </button>
            ))}
            <span className="text-[11px] text-gray-400 ml-auto">{models.activePreset ? `Currently: ${models.activePreset}` : 'Custom mix'}</span>
          </div>
          <div className="card mt-4 divide-y divide-gray-200 p-0">
            {[
              ['MODEL_ECONOMY', 'Economy · routine / high-volume'],
              ['MODEL_STANDARD', 'Standard'],
              ['MODEL_PREMIUM', 'Premium · reasoning'],
              ['MODEL_FRONTIER', 'Frontier · hardest work'],
              ['DEFAULT_LLM_MODEL', 'Default · chatbot / QA / billing / RAG'],
              ['FALLBACK_LLM_MODEL', 'Fallback · on error'],
            ].map(([key, label]) => {
              const liveId = models.live?.[key] || ''
              const val = mDraft[key] ?? liveId
              const tr = mTest[val]
              return (
                <div key={key} className="p-4 flex flex-wrap items-center gap-3">
                  <div className="min-w-[210px]">
                    <div className="text-sm font-semibold text-gray-900">{label}</div>
                    <div className="text-[11px] text-gray-400">{models.overrides?.[key]?.overridden ? 'admin-set (live)' : 'env / default'}</div>
                  </div>
                  <input className="input text-sm flex-1 min-w-[220px] font-mono" value={val}
                    onChange={(e) => setMDraft((d) => ({ ...d, [key]: e.target.value }))} placeholder="vendor/model" />
                  <button onClick={() => testModel(val)} className="btn-secondary text-xs px-3 py-1.5">Test</button>
                  <button onClick={() => saveModel(key, val)} disabled={mBusy === key || val === liveId}
                    className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">{mBusy === key ? 'Saving…' : 'Save'}</button>
                  {tr && (tr.loading
                    ? <span className="text-[11px] text-gray-400">testing…</span>
                    : <span className={`text-[11px] ${tr.ok ? 'text-emerald-600' : 'text-red-600'}`}>{tr.ok ? `ok · $${tr.cost ?? 0}` : String(tr.error || 'failed').slice(0, 40)}</span>)}
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Platform environment & secrets — encrypted in the DB, applied live */}
      <div className="flex items-center gap-2 mt-12">
        <KeyRound size={20} className="text-primary-600" />
        <h2 className="text-2xl font-bold text-gray-900">Environment &amp; secrets</h2>
      </div>
      <p className="text-gray-500 mt-1">Every platform variable — Stripe, email, integrations — managed here, <b>encrypted at rest</b> and applied within seconds (no redeploy, no <code className="text-xs bg-gray-100 px-1 rounded">.env</code> editing). Overrides the server's environment.</p>

      {/* Add a variable */}
      <div className="card mt-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">New variable — key</label>
          <input className="input font-mono text-sm" placeholder="STRIPE_SECRET_KEY" value={newKey}
            onChange={(e) => setNewKey(e.target.value.toUpperCase())} />
        </div>
        <div className="flex-[2] min-w-[220px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">Value</label>
          <input className="input text-sm" placeholder="value…" value={newValue} onChange={(e) => setNewValue(e.target.value)} />
        </div>
        <button onClick={addSetting} disabled={!newKey.trim()} className="btn-primary text-sm flex items-center gap-1 disabled:opacity-50">
          <Plus size={15} /> Add
        </button>
      </div>

      {settings === null ? (
        <div className="card mt-6 text-sm text-gray-500">Loading…</div>
      ) : Object.entries(groupsOf(settings)).map(([group, rows]) => (
        <div key={group} className="mt-6">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">{group}</p>
          <div className="card divide-y divide-gray-200 p-0">
            {rows.map((s) => (
              <div key={s.key} className="px-5 py-4 flex flex-wrap items-center gap-3">
                <div className="min-w-[220px]">
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    {s.label !== s.key ? s.label : s.key}
                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${sourceBadge(s.source)}`}>
                      {s.source === 'db' ? 'set' : s.source === 'env' ? 'from .env' : 'not set'}
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-400 font-mono">{s.key}{s.secret && s.preview ? ` · ${s.preview}` : ''}</div>
                </div>
                <input
                  className="input flex-1 min-w-[200px] text-sm"
                  type={s.secret && revealed[s.key] === undefined ? 'password' : 'text'}
                  placeholder={s.secret ? (s.source !== 'unset' ? 'Reveal to edit, or paste a new value' : 'Paste the value…') : (s.value || 'Enter a value…')}
                  value={draft[s.key] ?? (s.secret ? '' : s.value)}
                  onChange={(e) => setDraft(d => ({ ...d, [s.key]: e.target.value }))}
                />
                {s.secret && s.source !== 'unset' && (
                  <button onClick={() => reveal(s.key)} className="p-2 rounded hover:bg-gray-100 text-gray-500" title="Reveal"><Eye size={15} /></button>
                )}
                {s.source !== 'unset' && (
                  <button onClick={() => copyVal(s.key, s.value)} className="p-2 rounded hover:bg-gray-100 text-gray-500" title="Copy"><Copy size={15} /></button>
                )}
                <button onClick={() => saveSetting(s.key)} disabled={savingKey === s.key}
                  className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50">
                  {savingKey === s.key ? 'Saving…' : 'Save'}
                </button>
                {s.source === 'db' && (
                  <button onClick={() => removeSetting(s.key)} className="p-2 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={15} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-xs text-gray-400 mt-3">Secret values are AES-256-GCM encrypted. Use <b>Reveal</b> to view/copy; <b>Delete</b> removes a variable (falls back to the server env if one exists).</p>
    </div>
  )
}
